import { mdiPencil } from "@mdi/js";
import { Button, Col, Input, ModalProps, Row, Select, Switch } from "antd";
import {
  CreateFailureStatusCommand,
  FailureStatusVm,
  FieldRequireTypes,
} from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import { Permission } from "components/Permission";
import CustomForm from "components/form/CustomForm";
import CustomModal from "components/ui/CustomModal";
import Claims from "features/auth/claims";
import useEditFormModal from "hooks/useEditModal";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { enumTranslate } from "utils/enumTranslate";
import { GrapeAntdForm, requiredField, useGrapeAntdForm } from "widgets/form";
import { ListRequestParams } from "widgets/table/useTableUtils";

export interface StatusCreateModalProps extends ModalProps {
  fetchDataParams?: ListRequestParams;
}

const StatusCreateModal: FC<StatusCreateModalProps> = ({
  fetchDataParams,
  onCancel,
  visible,
}) => {
  const { t } = useTranslation();
  const formUtils = useGrapeAntdForm<any>();
  const dispatch = useDispatch();
  const { editingItemId } = useSelector((state) => state.statuses);
  const { list } = useSelector((state) => state.statuses);
  const { createStatus, editStatus } = useSelector(
    (state) => state.loading.effects.statuses
  );

  const getInitialValues = useCallback((itemData: FailureStatusVm) => {
    return {
      name: itemData.name || "",
      alternativeName: itemData.alternativeName || "",
      isSpecialCase: itemData.isSpecialCase,
      isSealed: itemData.isSealed,
      isDefault: itemData.isDefault,
      requiresWorkbookTask: itemData.requiresWorkbookTask,
      requiresStateChangeReason: itemData.requiresStateChangeReason,
    };
  }, []);

  const { formMode, setFormMode } = useEditFormModal<FailureStatusVm, any>({
    formUtils,
    getInitialValues,
    modalIsVisible: visible,
    resetItemId: dispatch.statuses.resetEditingItemData,
    itemId: editingItemId,
    list,
  });

  const handleFinish = useCallback(
    async (values: CreateFailureStatusCommand): Promise<void> => {
      if (formMode === "Create") {
        await dispatch.statuses.createStatus({
          ...values,
          requiresStateChangeReason:
            values.requiresStateChangeReason || FieldRequireTypes.Allowed,
        });
      }

      if (formMode === "Edit" && editingItemId) {
        await dispatch.statuses.editStatus({
          id: editingItemId,
          values,
        });
      }

      dispatch.statuses.fetchList(fetchDataParams);

      onCancel?.("" as any);
      formUtils.form.resetFields();
    },
    [
      dispatch.statuses,
      fetchDataParams,
      formMode,
      formUtils.form,
      onCancel,
      editingItemId,
    ]
  );

  return (
    <CustomModal
      visible={visible}
      onCancel={onCancel}
      title={
        formMode === "Create"
          ? t("settings.statuses.createModal.title")
          : formMode === "Edit"
          ? t("settings.statuses.editModal.title")
          : t("settings.statuses.modal.title")
      }
      size="large"
      onOk={formUtils.form.submit}
      destroyOnClose
      okButtonProps={{
        loading: createStatus.loading || editStatus.loading,
        disabled: formMode === "View",
      }}
      cancelButtonProps={{
        disabled: createStatus.loading || editStatus.loading,
      }}
      formInstance={formUtils.form}
    >
      <CustomForm
        formUtils={formUtils}
        onFinish={handleFinish}
        viewMode={formMode === "View"}
      >
        {formMode === "View" && (
          <Permission acceptableClaims={[Claims.FailureStatus.Write]}>
            <Row justify="end">
              <Button
                icon={<MaterialIcon path={mdiPencil} />}
                onClick={() => setFormMode("Edit")}
              >
                {t("common.edit")}
              </Button>
            </Row>
          </Permission>
        )}

        <Row gutter={[24, 0]}>
          <Col span={12}>
            <GrapeAntdForm.Item
              name="name"
              label={t("settings.statuses.name")}
              rules={[requiredField(t)]}
            >
              <Input size="large" />
            </GrapeAntdForm.Item>
          </Col>

          <Col span={12}>
            <GrapeAntdForm.Item
              name="alternativeName"
              label={t("settings.statuses.alternativeName")}
            >
              <Input size="large" />
            </GrapeAntdForm.Item>
          </Col>

          <Col span={12}>
            <GrapeAntdForm.Item
              name="isSpecialCase"
              valuePropName="checked"
              label={t("settings.statuses.isSpecialCase")}
            >
              <Switch
                checkedChildren={t("common.yes")}
                unCheckedChildren={t("common.no")}
              />
            </GrapeAntdForm.Item>
          </Col>

          <Col span={12}>
            <GrapeAntdForm.Item
              name="isSealed"
              valuePropName="checked"
              label={t("settings.statuses.isSealed")}
            >
              <Switch
                checkedChildren={t("common.yes")}
                unCheckedChildren={t("common.no")}
              />
            </GrapeAntdForm.Item>
          </Col>

          <Col span={12}>
            <GrapeAntdForm.Item
              name="isDefault"
              valuePropName="checked"
              label={t("settings.statuses.isDefault")}
            >
              <Switch
                checkedChildren={t("common.yes")}
                unCheckedChildren={t("common.no")}
              />
            </GrapeAntdForm.Item>
          </Col>

          <Col span={12}>
            <GrapeAntdForm.Item
              name="requiresWorkbookTask"
              valuePropName="checked"
              label={t("settings.statuses.requiresWorkbookTask")}
            >
              <Switch
                checkedChildren={t("common.yes")}
                unCheckedChildren={t("common.no")}
              />
            </GrapeAntdForm.Item>
          </Col>

          <Col span={12}>
            <GrapeAntdForm.Item
              name="requiresStateChangeReason"
              label={t("settings.statuses.requiresStateChangeReason")}
            >
              <Select
                size="large"
                options={Object.values(FieldRequireTypes).map((value) => ({
                  label: enumTranslate(value, "requiresStateChangeReason"),
                  value,
                }))}
                optionFilterProp="label"
                defaultValue={FieldRequireTypes.Allowed}
              />
            </GrapeAntdForm.Item>
          </Col>
        </Row>
      </CustomForm>
    </CustomModal>
  );
};

export default StatusCreateModal;
